/**
 * Shared API client for all services.
 * Single source of truth: base URL, auth header, and 401 refresh logic.
 * All endpoints stay in their respective services; only the HTTP client is shared.
 */

import axios from 'axios';
import { getApiUrl } from '../config/environments';

/**
 * Base URL for all API requests (from REACT_APP_API_URL or env config).
 * Paths in the app always start with /api/web-api/..., so we strip a trailing /api or /api/
 * from the env URL to avoid double /api/api/ when env is set to e.g. .../api.
 */
const rawBase = getApiUrl();
export const apiBaseUrl =
  typeof rawBase === 'string' ? rawBase.replace(/\/api\/?$/, '') : rawBase;

/** Axios instance with baseURL and credentials; used by all services for real API calls */
export const apiClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

/**
 * Auth scheme for Authorization header. Some backends (e.g. Django REST TokenAuthentication)
 * expect "Token <key>"; others (e.g. JWT) expect "Bearer <key>".
 * Set REACT_APP_AUTH_HEADER_SCHEME=Token if the API returns "Authentication credentials were not provided" with Bearer.
 */
const AUTH_HEADER_SCHEME =
  process.env.REACT_APP_AUTH_HEADER_SCHEME === 'Token' ? 'Token' : 'Bearer';

function getAccessToken() {
  return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

// Request interceptor: add Authorization header (consistent across all services)
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers['Authorization'] = `${AUTH_HEADER_SCHEME} ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Refresh access token (cookie-based or returns new token in body).
 * Used by the 401 interceptor and by authThunks / AuthContext.
 * If the response contains access_token, it is stored so the next request uses it.
 * @returns {Promise<{ access_token?: string }>} Response data from refresh endpoint
 */
export async function refreshToken() {
  const response = await axios.post(
    `${apiBaseUrl}/api/web-api/refresh_token`,
    null,
    { withCredentials: true },
  );
  const data = response.data;
  if (data?.access_token) {
    if (localStorage.getItem('accessToken')) {
      localStorage.setItem('accessToken', data.access_token);
    } else {
      sessionStorage.setItem('accessToken', data.access_token);
    }
  }
  return data;
}

// Response interceptor: on 401, try refresh then retry once (same auth scheme as request)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await refreshToken();
        const accessToken = getAccessToken();
        if (accessToken) {
          originalRequest.headers['Authorization'] = `${AUTH_HEADER_SCHEME} ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);
